import { useEffect, useMemo, useState } from "react";
import { CircleDollarSign, ExternalLink, ShieldCheck } from "lucide-react";
import { chiNotify } from "@/components/ChiNotification";
import { specialEventPricingLock } from "@/components/SpecialEventApprovalControl";
import { trpc } from "@/lib/trpc";
import HomepagePromotionControl from "@/components/HomepagePromotionControl";
import EventTicketDiscountControl from "@/components/EventTicketDiscountControl";

const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "rgba(255,255,255,0.055)",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: "0.5rem",
  padding: "0.55rem 0.7rem",
  color: "white",
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: "0.8rem",
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  marginBottom: ".34rem",
  color: "#64748B",
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: ".65rem",
  fontWeight: 700,
  letterSpacing: ".08em",
  textTransform: "uppercase",
};

export default function EventRegistrationTypeControl({
  eventSlug,
  readOnly = false,
  isSpecialEvent = false,
}: {
  eventSlug: string;
  /** Special events (manual approval) are free-only; the server refuses paid pricing for them too. */
  isSpecialEvent?: boolean;
  /**
   * Shows the saved policy without offering to change it.
   *
   * The drawer opens on a record, not a form, and this section was the one place
   * where a price and a checkout URL could be edited and saved before anyone had
   * pressed Edit event — a different rule from every other field beside it.
   */
  readOnly?: boolean;
}) {
  const utils = trpc.useUtils();
  const policiesQuery = trpc.events.pricingPolicies.useQuery(undefined, { retry: false });
  const policy = useMemo(
    () => policiesQuery.data?.find(item => item.eventSlug === eventSlug),
    [policiesQuery.data, eventSlug],
  );
  const [registrationType, setRegistrationType] = useState<"free" | "paid_external" | "paid_internal">("free");
  const [priceLabel, setPriceLabel] = useState("Complimentary");
  const [priceAmount, setPriceAmount] = useState("");
  const [stripePriceId, setStripePriceId] = useState("");
  const [externalCheckoutUrl, setExternalCheckoutUrl] = useState("");
  const [publicNotice, setPublicNotice] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!policy) {
      setRegistrationType("free");
      setPriceLabel("Complimentary");
      setPriceAmount("");
      setStripePriceId("");
      setExternalCheckoutUrl("");
      setPublicNotice("");
      return;
    }
    setRegistrationType(policy.pricingMode);
    setPriceLabel(policy.priceLabel);
    setPriceAmount(policy.priceAmountCents ? (policy.priceAmountCents / 100).toFixed(2) : "");
    setStripePriceId(policy.stripePriceId ?? "");
    setExternalCheckoutUrl(policy.externalCheckoutUrl ?? "");
    setPublicNotice(policy.publicNotice ?? "");
  }, [policy, eventSlug]);

  const pricingLock = specialEventPricingLock(isSpecialEvent);

  const savePolicy = trpc.events.savePricingPolicy.useMutation({
    onSuccess: async saved => {
      await utils.events.pricingPolicies.invalidate();
      setError(null);
      chiNotify.success("Registration type saved", {
        description: saved.pricingMode === "free"
          ? "This event is configured as Free."
          : saved.pricingMode === "paid_external"
            ? "This event is configured as Paid with an external checkout route."
            : "This event is configured for secure Stripe Checkout.",
      });
    },
    onError: mutationError => {
      const message = mutationError.message || "The registration type could not be saved.";
      setError(message);
      chiNotify.error("Registration type not saved", { description: message });
    },
  });

  function save(event: React.FormEvent) {
    event.preventDefault();
    if (pricingLock && registrationType !== "free") {
      setError(pricingLock);
      return;
    }
    if (registrationType === "paid_external") {
      try {
        const url = new URL(externalCheckoutUrl);
        if (url.protocol !== "https:") throw new Error("Use an HTTPS checkout URL for paid registration.");
      } catch (validationError) {
        setError(validationError instanceof Error ? validationError.message : "Enter a secure checkout URL for paid registration.");
        return;
      }
    }
    let priceAmountCents: number | undefined;
    if (registrationType === "paid_internal" && !stripePriceId.trim()) {
      const dollars = Number(priceAmount);
      if (!Number.isFinite(dollars) || dollars < 0.5) {
        setError("Enter an amount of at least $0.50 or provide a Stripe Price ID.");
        return;
      }
      priceAmountCents = Math.round(dollars * 100);
    }
    setError(null);
    savePolicy.mutate({
      eventSlug,
      registrationType,
      priceLabel: priceLabel.trim() || (registrationType === "free" ? "Complimentary" : "Paid registration"),
      ...(registrationType === "paid_internal" ? {
        ...(priceAmountCents ? { priceAmountCents } : {}),
        currency: "usd",
        ...(stripePriceId.trim() ? { stripePriceId: stripePriceId.trim() } : {}),
      } : {}),
      ...(registrationType === "paid_external" ? { externalCheckoutUrl: externalCheckoutUrl.trim() } : {}),
      ...(publicNotice.trim() ? { publicNotice: publicNotice.trim() } : {}),
    });
  }

  return (
    <>
    <form onSubmit={save} style={{ background: "rgba(0,212,255,.045)", border: "1px solid rgba(0,212,255,.17)", borderRadius: ".65rem", padding: ".95rem 1rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: ".45rem", marginBottom: ".7rem" }}>
        <CircleDollarSign size={15} style={{ color: "#7DEAFF" }} />
        <span style={{ color: "#7DEAFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".68rem", fontWeight: 750, letterSpacing: ".1em", textTransform: "uppercase" }}>Registration type</span>
        {policy && <span style={{ marginLeft: "auto", padding: ".24rem .45rem", borderRadius: "999px", color: policy.pricingMode === "free" ? "#86EFAC" : "#FCD34D", background: policy.pricingMode === "free" ? "rgba(74,222,128,.08)" : "rgba(245,166,35,.08)", border: `1px solid ${policy.pricingMode === "free" ? "rgba(74,222,128,.22)" : "rgba(245,166,35,.22)"}`, fontFamily: "'Space Grotesk', sans-serif", fontSize: ".64rem", fontWeight: 700 }}>{policy.pricingMode === "free" ? "FREE" : "PAID"}</span>}
      </div>
      <p style={{ margin: "0 0 .85rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>Choose Free, Paid — External Ticketing, or Paid — Internal Stripe Checkout. Internal checkout sends visitors to Stripe and confirms registration only after the signed payment webhook succeeds.</p>
      <HomepagePromotionControl key={eventSlug} eventSlug={eventSlug} readOnly={readOnly} />
      <div style={{ display: "grid", gridTemplateColumns: "minmax(150px,.7fr) minmax(0,1.3fr)", gap: ".65rem" }} className="event-pricing-grid">
        <label>
          <span style={labelStyle}>Type</span>
          <select disabled={readOnly} value={registrationType} onChange={event => { const next = event.target.value as "free" | "paid_external" | "paid_internal"; setRegistrationType(next); if (next === "free" && !priceLabel.trim()) setPriceLabel("Complimentary"); }} style={{ ...inputStyle, cursor: "pointer" }}>
            <option value="free">Free</option>
            <option value="paid_external" disabled={isSpecialEvent && registrationType !== "paid_external"}>Paid — External Ticketing</option>
            <option value="paid_internal" disabled={isSpecialEvent && registrationType !== "paid_internal"}>Paid — Internal Stripe Checkout</option>
          </select>
        </label>
        <label>
          <span style={labelStyle}>{registrationType === "free" ? "Public label" : "Price or paid label"}<span aria-hidden="true" style={{ color: "#F9A8D4", marginLeft: ".18rem" }}>*</span></span>
          <input disabled={readOnly} required aria-required="true" value={priceLabel} onChange={event => setPriceLabel(event.target.value)} maxLength={120} style={inputStyle} placeholder="Enter how the price should read to attendees" />
        </label>
      </div>
      {pricingLock && !readOnly && <p role="status" style={{ margin: ".65rem 0 0", color: "#FCD34D", fontFamily: "'Inter', sans-serif", fontSize: ".74rem", lineHeight: 1.45 }}>{pricingLock}</p>}
      {registrationType === "paid_external" && <label style={{ display: "block", marginTop: ".65rem" }}>
        <span style={labelStyle}>External checkout URL<span aria-hidden="true" style={{ color: "#F9A8D4", marginLeft: ".18rem" }}>*</span></span>
        <div style={{ position: "relative" }}><ExternalLink size={13} style={{ position: "absolute", top: ".66rem", left: ".7rem", color: "#7DEAFF" }} /><input disabled={readOnly} required aria-required="true" type="url" value={externalCheckoutUrl} onChange={event => setExternalCheckoutUrl(event.target.value)} style={{ ...inputStyle, paddingLeft: "2rem" }} placeholder="Enter the full web address" /></div>
      </label>}
      {registrationType === "paid_internal" && <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: ".65rem", marginTop: ".65rem" }} className="event-pricing-grid">
        <label>
          <span style={labelStyle}>Amount in USD {!stripePriceId.trim() && <span aria-hidden="true" style={{ color: "#F9A8D4" }}>*</span>}</span>
          <input disabled={readOnly} required={!stripePriceId.trim()} aria-required={!stripePriceId.trim() || undefined} inputMode="decimal" value={priceAmount} onChange={event => setPriceAmount(event.target.value)} style={inputStyle} placeholder="Enter the amount in dollars" aria-describedby="internal-price-help" />
        </label>
        <label>
          <span style={labelStyle}>Stripe Price ID (optional)</span>
          <input disabled={readOnly} value={stripePriceId} onChange={event => setStripePriceId(event.target.value)} style={inputStyle} placeholder="Enter the Stripe price ID" />
        </label>
        <p id="internal-price-help" role="status" style={{ gridColumn: "1 / -1", margin: 0, padding: ".65rem .75rem", borderRadius: ".5rem", background: "rgba(74,222,128,.07)", border: "1px solid rgba(74,222,128,.2)", color: "#86EFAC", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>Enter a USD amount, or provide an existing Stripe Price ID. Card details are collected only on Stripe's hosted checkout page.</p>
      </div>}
      <label style={{ display: "block", marginTop: ".65rem" }}>
        <span style={labelStyle}>Public registration note (optional)</span>
        <input disabled={readOnly} value={publicNotice} onChange={event => setPublicNotice(event.target.value)} maxLength={500} style={inputStyle} placeholder={registrationType === "free" ? "Free registration is available." : "You will continue to secure checkout."} />
      </label>
      {error && <p role="alert" style={{ margin: ".65rem 0 0", color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: ".74rem", lineHeight: 1.45 }}>{error}</p>}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".7rem", marginTop: ".8rem", flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: ".35rem", color: "#AEB9C9", fontFamily: "'Inter', sans-serif", fontSize: ".7rem" }}><ShieldCheck size={13} /> Payment success is recorded only after Stripe signature verification.</span>
        {!readOnly && <button type="submit" disabled={savePolicy.isPending || policiesQuery.isLoading || (isSpecialEvent && registrationType !== "free")} style={{ minHeight: 36, padding: ".45rem .65rem", border: "1px solid rgba(0,212,255,.32)", borderRadius: ".5rem", background: "rgba(0,212,255,.10)", color: "#7DEAFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".72rem", fontWeight: 700, cursor: savePolicy.isPending ? "wait" : "pointer" }}>{savePolicy.isPending ? "Saving…" : "Save registration type"}</button>}
      </div>
    </form>
    <EventTicketDiscountControl key={eventSlug} eventSlug={eventSlug} readOnly={readOnly} isSpecialEvent={isSpecialEvent} />
    </>
  );
}
